import { Injectable } from '@angular/core';
import { HttpEvent, HttpRequest } from '@angular/common/http';
import { EMPTY, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

const MUTATING_METHODS = new Set<string>(['POST', 'DELETE']);

type RequestBody = null | unknown[] | Record<string, unknown>;

@Injectable({
  providedIn: 'root'
})
export class InFlightDedupService {
  private readonly inFlightKeys = new Set<string>();

  public handle$(
    request: HttpRequest<unknown>,
    next: (req: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>
  ): Observable<HttpEvent<unknown>> {
    if (!MUTATING_METHODS.has(request.method)) {
      return next(request);
    }

    const uniqueKey = this.buildUniqueRequestKey(request);
    if (this.inFlightKeys.has(uniqueKey)) {
      return EMPTY;
    }

    this.inFlightKeys.add(uniqueKey);
    return next(request).pipe(
      finalize((): void => {
        this.inFlightKeys.delete(uniqueKey);
      })
    );
  }

  private buildUniqueRequestKey(request: HttpRequest<unknown>): string {
    const body = request.body as RequestBody;
    const bodyAsString = this.stableStringifyBody(body);
    return `${request.method}:${request.urlWithParams}:${bodyAsString}`;
  }

  private stableStringifyBody(body: RequestBody | unknown): string {
    if (body === null || body === undefined) {
      return '';
    }
    if (Array.isArray(body)) {
      return (
        '[' + body.map((item: unknown): string => this.stableStringifyBody(item)).join(',') + ']'
      );
    }
    if (typeof body === 'object') {
      return this.stringifyObjectWithSortedKeys(body);
    }
    return JSON.stringify(body);
  }

  private stringifyObjectWithSortedKeys(obj: object): string {
    const sortedKeys = Object.keys(obj).sort();
    const record = obj as Record<string, unknown>;
    const entries = sortedKeys.map(
      (k: string): string => JSON.stringify(k) + ':' + this.stableStringifyBody(record[k])
    );
    return '{' + entries.join(',') + '}';
  }
}
